import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { InputNumberModule } from 'primeng/inputnumber';
import { ColorStop, Gradient } from './gradient-picker.types';
import { GradientCssPipe } from './gradient-css.pipe';
import { MenubarModule } from "primeng/menubar";
import { Select } from "primeng/select";

@Component({
    selector: 'app-gradient-picker',
    imports: [CommonModule, FormsModule, InputTextModule, InputGroupModule, InputGroupAddonModule, InputNumberModule, GradientCssPipe, MenubarModule, Select],
    templateUrl: './gradient-picker.component.html',
    styleUrls: ['./gradient-picker.component.css']
})
export class GradientPickerComponent implements OnChanges {
  @Input() gradient: Gradient | null = null;
  @Input() defaultGradient: Gradient = {
    angle: 135,
    gradientType: "linear",
    colors: [
      { color: '#7322c3', position: 0 },
      { color: '#bf5af2', position: 100 },
    ]
  }

  colorStops: ColorStop[] | null = [
    { color: '#7322c3', position: 0 },
    { color: '#bf5af2', position: 100 },
  ];
  angle: number = 135;
  gradientType: string = 'linear';
  @Input() fieldsToShow: ("rotation" | "type" | "reset")[] = ["rotation", "reset"]
  @Input() maxColors: number = 2;
  minColors: number = 2;

  @Output() gradientChange = new EventEmitter<Gradient>();

  @ViewChild('stopBar') stopBar!: ElementRef;

  selectedStopIndex: number | null = 0;
  copied = false;
  isDragging = false;
  dragIndex = -1;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gradient']?.currentValue && this.gradient) {
      this.colorStops = this.gradient.colors ?? this.defaultGradient.colors;
      this.angle = this.gradient.angle ?? this.defaultGradient.angle;
      this.gradientType = this.gradient.gradientType ?? this.defaultGradient.gradientType;
    }
  }

  getGradiantValue(): Gradient {
    return {
      angle: this.angle,
      gradientType: this.gradientType,
      colors: this.colorStops || []
    }
  }

  getGradientCss(): string {
    if(!this.colorStops) {
      return "";
    }

    const sortedStops = [...this.colorStops].sort(
      (a, b) => a.position - b.position
    );
    const colorStops = sortedStops
      .map((stop) => `${stop.color} ${stop.position}%`)
      .join(', ');

    if (this.gradientType === 'radial') {
      return `radial-gradient(circle, ${colorStops})`;
    }
    return `linear-gradient(${this.angle}deg, ${colorStops})`;
  }

  addColorAtPosition(event: MouseEvent): void {
    if (this.colorStops && this.colorStops.length >= this.maxColors) {
      return;
    }

    if (
      (event.target as HTMLElement).classList.contains('color-stop') ||
      (event.target as HTMLElement).classList.contains('stop-handle')
    ) {
      return;
    }

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const position = Math.round(
      ((event.clientX - rect.left) / rect.width) * 100
    );

    const randomColor =
      '#' +
      Math.floor(Math.random() * 16777215)
        .toString(16)
        .padStart(6, '0');

    this.colorStops?.push({
      color: randomColor,
      position: Math.max(0, Math.min(100, position)),
    });

    this.selectedStopIndex = (this.colorStops?.length ?? 1) - 1;
    this.onGradientChange();
  }

  selectStop(index: number, event: MouseEvent): void {
    event.stopPropagation();
    this.selectedStopIndex = index;
  }

  removeColorStop(index: number, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.colorStops?.splice(index, 1);
    this.selectStop(0, event);
    this.onGradientChange();
  }

  startDrag(index: number, event: MouseEvent): void {
    if(!this.colorStops) return;

    event.preventDefault();
    event.stopPropagation();

    this.selectedStopIndex = index;
    this.isDragging = true;
    this.dragIndex = index;

    const onMouseMove = (e: MouseEvent) => {
      if (!this.isDragging || !this.stopBar) return;

      const rect = this.stopBar.nativeElement.getBoundingClientRect();
      const position = ((e.clientX - rect.left) / rect.width) * 100;
      this.colorStops![this.dragIndex].position = Math.max(
        0,
        Math.min(100, Math.round(position))
      );
      this.onGradientChange();
    };

    const onMouseUp = () => {
      this.isDragging = false;
      this.dragIndex = -1;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  resetGradient() {
    this.angle = this.defaultGradient.angle;
    this.gradientType = this.defaultGradient.gradientType;
    this.colorStops = this.defaultGradient.colors;
    this.onGradientChange();
  }

  validateColorInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.trim();

    // Always ensure it starts with #
    if (!value.startsWith('#')) {
      value = '#' + value;
    }

    // Only allow 0-9 and a-f characters
    value = value.replace(/[^#0-9a-fA-F]/g, '');

    // Limit to 7 characters (# + 6 digits)
    if (value.length > 7) {
      value = value.substring(0, 7);
    }

    // Update model
    this.colorStops![this.selectedStopIndex!].color = value;
    (event.target as HTMLInputElement).value = value;
    this.onGradientChange();
  }

  onGradientChange(): void {
    this.gradientChange.emit({
      angle: this.angle,
      gradientType: this.gradientType,
      colors: this.colorStops || []
    });
  }

  randomize(): void {
    this.colorStops = this.colorStops?.map((stop) => ({
      ...stop,
      color:
        '#' +
        Math.floor(Math.random() * 16777215)
          .toString(16)
          .padStart(6, '0'),
    })) || [];
    this.onGradientChange();
  }

  copyToClipboard(): void {
    navigator.clipboard.writeText(`background: ${this.getGradientCss()};`);
    this.copied = true;
    setTimeout(() => (this.copied = false), 2000);
  }
}
